import { useCallback } from 'react';

import { useSnackbarNotifications as useSnackbarNotificationsContext } from './snackbarNotifications.context';

export { SnackbarNotificationsProvider } from './snackbarNotifications.context';
export type { SnackbarMessage } from './snackbarNotifications.context';
export { useSnackbarNotificationsContext };

/**
 * Legacy hook for backward compatibility.
 * Uses snackIdRef to track a single notification at a time.
 *
 * For new code, consider using useSnackbarNotificationsContext
 * with requestKey pattern to manage multiple concurrent notifications.
 */
type SnackbarNotificationsResult = {
  showErrorSnackbar: (message: string, autoHideDuration?: number, key?: string) => string;
  showLoadingSnackbar: (message: string, key?: string) => string;
  showSuccessSnackbar: (message: string, key?: string) => string;
};

const useSnackbarNotifications = (): SnackbarNotificationsResult => {
  const contextFunctions = useSnackbarNotificationsContext();

  const showErrorSnackbar = useCallback(
    (message: string, autoHideDuration?: number, key?: string): string => {
      // Use provided key or generate a default one
      const requestKey = key || 'default';
      return contextFunctions.showErrorSnackbar(requestKey, message, autoHideDuration);
    },
    [contextFunctions]
  );

  const showLoadingSnackbar = useCallback(
    (message: string, key?: string): string => {
      const requestKey = key || 'default';
      return contextFunctions.showLoadingSnackbar(requestKey, message);
    },
    [contextFunctions]
  );

  const showSuccessSnackbar = useCallback(
    (message: string, key?: string): string => {
      const requestKey = key || 'default';
      return contextFunctions.showSuccessSnackbar(requestKey, message);
    },
    [contextFunctions]
  );

  return {
    showErrorSnackbar,
    showLoadingSnackbar,
    showSuccessSnackbar
  };
};

export default useSnackbarNotifications;
